import React, { useCallback, useEffect, useRef, useState } from 'react';
import classes from './OverflowMenu.module.css';
import './overflowQuill.css';

/**
 * The ... trigger and its popover.
 *
 * Slide commands are React and render the very same element here as on the row,
 * so their dropdowns - which mount in a portal - behave identically either way.
 * Quill's plain buttons are real nodes the adapter moves in; its pickers never
 * come here at all.
 *
 * Lives inside #my-toolbar on purpose: MasterSlide's click-away handler treats
 * anything the toolbar contains as a toolbar click.
 */
export function OverflowMenu({ commands, quillCount, quillMountRef }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const panelRef = useRef(null);

  // The menu is absent entirely while the row still fits everything, so effects
  // that touch the panel have to re-run when it comes and goes.
  const hasContent = commands.length > 0 || quillCount > 0;

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      const root = rootRef.current;
      if (!root || root.contains(event.target)) return;

      // Shapes, Layout, Slide colour and the rest are antd Dropdowns, and antd
      // renders an overlay in a portal attached to <body> - outside the app
      // root. Clicking an item in one of those is a click *on this menu's own
      // dropdown*, so closing here would unmount the trigger and destroy the
      // overlay before its handler ever ran, which looked like nothing
      // happening at all.
      const appRoot = document.getElementById('root');
      if (appRoot && !appRoot.contains(event.target)) return;

      close();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  // Editor.js binds exactly this to Quill's own toolbar so that clicking a
  // control never blurs the editor. Everything in this menu needs it too:
  // losing focus deselects the text element, which tears Quill's toolbar out of
  // #quillToolbar - and with ~1400px of demand suddenly gone, nothing overflows
  // any more and the menu removes itself mid-click. A native listener rather
  // than a React prop, because the Quill buttons moved in here are not React's.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return undefined;

    const keepSelection = (event) => event.preventDefault();
    panel.addEventListener('mousedown', keepSelection);
    return () => panel.removeEventListener('mousedown', keepSelection);
  }, [hasContent]);

  // Clicking a row's label should do what its control does. Guarded to the
  // label so a click on the control itself is not fired twice.
  const onPanelClick = (event) => {
    const label = event.target.closest && event.target.closest('.ovm-row-label, .' + classes.label);
    if (!label) return;

    const row = label.parentElement;
    if (!row) return;

    // Order matters. A command like Layout renders <button><Dropdown/></button>,
    // and clicking the outer button never reaches antd's trigger inside it - so
    // the dropdown would not open. Aim at the trigger itself when there is one.
    const control =
      row.querySelector('.ant-dropdown-trigger') ||
      row.querySelector('label[for]') ||
      row.querySelector('button, [role="button"]');

    if (control) control.click();
  };

  if (!hasContent) return null;

  return (
    // SmartArtObject ends node editing on any mousedown outside the SmartArt,
    // exempting only [data-smartart-node-toolbar]. That marker has to cover the
    // whole menu - trigger included - or reaching for ... while editing a node
    // unmounts the editor, and its cleanup removes the Quill toolbar outright,
    // leaving the toolbar area empty.
    <div className={classes.root} ref={rootRef} data-smartart-node-toolbar="true">
      <button
        type="button"
        className={classes.trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More toolbar commands"
        title="More commands"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        <span aria-hidden="true">&#8943;</span>
      </button>

      <div
        className={classes.panel}
        role="menu"
        hidden={!open}
        ref={panelRef}
        onClick={onPanelClick}
      >
        {commands.length > 0 && <div className={classes.group}>Slide</div>}
        {commands.map((command) => (
          <div className={classes.row} role="menuitem" key={command.id}>
            {/* A command may offer a different trigger for the menu - Layout
                spells its own name on the row, which would read twice here. */}
            {command.menuNode || command.node}
            {/* Master Slide's control is its own text, so labelling it again
                would just print the words twice. */}
            {!command.selfLabelling && <span className={classes.label}>{command.label}</span>}
          </div>
        ))}

        <div className={classes.group} hidden={!quillCount}>Text format</div>
        {/* ql-snow/ql-toolbar so Quill's own theme styles the buttons that move
            in. The SmartArt marker is on the root, so it covers this too. */}
        <div className="ovm-quill ql-snow ql-toolbar" ref={quillMountRef} />
      </div>
    </div>
  );
}

export default OverflowMenu;
